import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { CreativeTypes, ModelMetadata } from '@bynder-studio/render-core';
import { useDispatch } from 'react-redux';
import { Divider, Dropdown, Toolbar } from '@bynder/design-system';
import { useTranslate } from '@bynder/localization';
import { IconAdd } from '@bynder/icons';
import MultiSizeModal from 'packages/common/MultiSizeModal';
import useEditor from '~/hooks/useEditor';
import PageItem from './components/PageItem';
import PageItemName from './components/PageItemName';
import OptionsDropdown from './components/OptionsDropdown';
import DeleteModal from './components/PageDeleteModal';
import generateTestId from 'packages/helpers/testIdHelpers';
import PageRenameModal from './components/PageRenameModal';
import PageDuplicateModal from './components/PageDuplicateModal';
import { markAsUnsaved } from 'packages/store/creativeEditor/creativeEditor.actions';
import PageManageModal from './components/PageManageModal';
import { PagePreviewProvider } from '~/contexts/PagePreviewContext';
import { PageMeta } from './types';

const MultiPageDropdown = ({ isInAction }: { isInAction: boolean }) => {
    const { creativeModel, creativeType, setModalActive } = useEditor();
    const [pagesMetaData, setPagesMetaData] = useState<PageMeta[]>([]);
    const [selectedPageIndex, setSelectedPageIndex] = useState(creativeModel.getCurrentPageIndex());
    const [isDuplicateModalOpened, setIsDuplicateModalOpened] = useState(false);
    const [isPageModalOpened, setIsPageModalOpened] = useState(false);
    const [isRenameModalOpened, setIsRenameModalOpened] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isPageManageOpen, setIsPageManageOpen] = useState(false);
    const isVideoModel = creativeType === 'VIDEO';
    const dispatch = useDispatch();
    const { translate } = useTranslate();

    const sortPagesMetaData = useCallback(() => {
        const models = creativeModel.getModels();
        const sortedPages = creativeModel
            .getModelsMetaData()
            .map((page, index) => ({
                ...page,
                index,
                dimensions: models[index].getDimensions(),
            }))
            .sort((a, b) => a.displayOrder - b.displayOrder);

        if (sortedPages) {
            setPagesMetaData(sortedPages);
        }
    }, [creativeModel]);

    const handlePageModal = useCallback(() => {
        setIsPageModalOpened(!isPageModalOpened);
    }, [isPageModalOpened]);

    const handlePageSelect = useCallback(
        (index) => {
            creativeModel.setCurrentPageIndex(index);
            setSelectedPageIndex(index);
        },
        [creativeModel],
    );

    const handleOnPageRename = useCallback(
        (name) => {
            creativeModel.renamePage(selectedPageIndex, name);
            sortPagesMetaData();
            setIsRenameModalOpened(false);
            dispatch(markAsUnsaved());
        },
        [creativeModel, dispatch, selectedPageIndex],
    );

    const handleOnPageManage = (updatedPagesMeta: ModelMetadata[]) => {
        const currentPageOrder = pagesMetaData.map((page) => page.id);
        const updatedPageOrder = updatedPagesMeta.map((page) => page.id);
        const updatedIndexes = currentPageOrder.reduce((acc, id) => {
            const index = updatedPageOrder.indexOf(id);

            if (index !== -1) {
                acc.push(index);
            }

            return acc;
        }, [] as number[]);

        pagesMetaData.forEach((pageMeta) => {
            if (!updatedPageOrder.includes(pageMeta.id)) {
                const removedIndex = creativeModel.getModelsMetaData().findIndex((meta) => meta.id === pageMeta.id);

                if (removedIndex === -1) {
                    return;
                }

                creativeModel.removePage(removedIndex);
            }
        });

        // Rearrange models
        creativeModel.updatePageOrder(updatedIndexes, updatedPagesMeta);

        dispatch(markAsUnsaved());
        setIsPageManageOpen(false);
    };

    const handleSizeModalConfirm = (name, { width, height, format }) => {
        creativeModel.addPage(name, width, height, format);
        dispatch(markAsUnsaved());
        setIsPageModalOpened(false);
    };

    const handleDuplicateModalConfirm = (name, { width, height, format }, contentPropertiesSettings) => {
        const duplicationData = {
            name,
            dimensions: { width, height, format },
        };

        creativeModel.duplicatePage(selectedPageIndex, duplicationData, contentPropertiesSettings);
        setIsDuplicateModalOpened(false);
        dispatch(markAsUnsaved());
    };

    const handleOptionSelect = (option) => {
        switch (option) {
            case 'rename':
                setIsRenameModalOpened(true);
                break;
            case 'duplicate':
                setIsDuplicateModalOpened(true);
                break;
            case 'delete':
                setIsDeleteModalOpen(true);
                break;
            default:
        }
    };

    const handleDeleteModalConfirm = useCallback(() => {
        creativeModel.removePage(selectedPageIndex);
        setIsDeleteModalOpen(false);
        dispatch(markAsUnsaved());
    }, [creativeModel, dispatch, selectedPageIndex]);

    const handleDeleteModalClose = useCallback(() => {
        setIsDeleteModalOpen(false);
    }, []);

    useEffect(() => {
        const handlePageAdded = ({ metaData, pageIndex }) => {
            const pageMetaData = {
                ...metaData,
                displayOrder: pageIndex + 1,
            } as PageMeta;

            const filteredMeta = pagesMetaData.filter((page) => page.id !== pageMetaData.id);
            const pages = [...filteredMeta, pageMetaData];

            setPagesMetaData(pages);
            dispatch(markAsUnsaved());
        };

        const handleCurrentSizeChange = (index) => {
            setSelectedPageIndex(index);
        };

        const handlePageMetaChanges = () => sortPagesMetaData();

        creativeModel.getEventEmitter().on('currentPageChange', handleCurrentSizeChange);
        creativeModel.getEventEmitter().on('pageAdded', handlePageAdded);
        creativeModel.getEventEmitter().on('pageRenamed', handlePageMetaChanges);
        creativeModel.getEventEmitter().on('pageRemoved', handlePageMetaChanges);
        creativeModel.getEventEmitter().on('pageOrderUpdated', handlePageMetaChanges);

        return () => {
            creativeModel.getEventEmitter().off('currentPageChange', handleCurrentSizeChange);
            creativeModel.getEventEmitter().off('pageAdded', handlePageAdded);
            creativeModel.getEventEmitter().off('pageRenamed', handlePageMetaChanges);
            creativeModel.getEventEmitter().off('pageRemoved', handlePageMetaChanges);
            creativeModel.getEventEmitter().off('pageOrderUpdated', handlePageMetaChanges);
        };
    }, [creativeModel, dispatch, pagesMetaData]);

    useEffect(() => {
        sortPagesMetaData();
    }, [creativeModel.getModelsMetaData()]);

    return (
        <>
            <Toolbar>
                <Dropdown
                    trigger={({ isOpen, ...triggerProps }) => (
                        <PageItemName
                            triggerProps={triggerProps}
                            sizeMetaData={pagesMetaData[selectedPageIndex]}
                            isVideoModel={isVideoModel}
                        />
                    )}
                    {...generateTestId('editor-multipage-dropdown')}
                >
                    {pagesMetaData?.map((pageMeta) => {
                        const { name, displayOrder, id } = pageMeta;
                        const pageIndex = displayOrder - 1;
                        const isSelected = pageIndex === selectedPageIndex;

                        return (
                            <Fragment key={id}>
                                <PageItem
                                    sizeIndex={pageIndex}
                                    name={name}
                                    isSelected={isSelected}
                                    onSelect={handlePageSelect}
                                    sizeMetaData={pageMeta}
                                    isVideoModel={isVideoModel}
                                />
                            </Fragment>
                        );
                    })}
                    <Dropdown.Item icon={<IconAdd />} onClick={handlePageModal}>
                        {translate('editor.page.dropdown.new')}
                    </Dropdown.Item>
                    <Divider />
                    <Dropdown.Item
                        onClick={() => {
                            setIsPageManageOpen(true);
                            setModalActive(true);
                        }}
                    >
                        {translate('editor.page.dropdown.manage')}
                    </Dropdown.Item>
                </Dropdown>
                <Toolbar.Divider />
                <OptionsDropdown onSelect={handleOptionSelect} />
            </Toolbar>
            {isDuplicateModalOpened && (
                <PageDuplicateModal
                    pageMetaData={pagesMetaData[selectedPageIndex]}
                    creativeModel={creativeModel}
                    onToggle={setIsDuplicateModalOpened}
                    handleConfirm={handleDuplicateModalConfirm}
                    creativeType={CreativeTypes[creativeType]}
                    isInAction={isInAction}
                />
            )}
            <DeleteModal
                show={isDeleteModalOpen}
                onConfirm={handleDeleteModalConfirm}
                onClose={handleDeleteModalClose}
                pageName={pagesMetaData[selectedPageIndex]?.name || ''}
                isInAction={isInAction}
            />
            {isPageModalOpened && (
                <MultiSizeModal
                    creativeModel={creativeModel}
                    onToggle={handlePageModal}
                    handleConfirm={handleSizeModalConfirm}
                    creativeType={CreativeTypes[creativeType]}
                    isInAction={isInAction}
                />
            )}
            {isRenameModalOpened && (
                <PageRenameModal
                    show={isRenameModalOpened}
                    pageName={pagesMetaData[selectedPageIndex]?.name}
                    onClose={() => setIsRenameModalOpened(false)}
                    onConfirm={handleOnPageRename}
                    isInAction={isInAction}
                />
            )}
            {isPageManageOpen && (
                <PagePreviewProvider isEditor>
                    <PageManageModal
                        pages={pagesMetaData}
                        show={isPageManageOpen}
                        onClose={() => setIsPageManageOpen(false)}
                        onConfirm={handleOnPageManage}
                        isInAction={isInAction}
                    />
                </PagePreviewProvider>
            )}
        </>
    );
};

export default MultiPageDropdown;
